import React from "react";
import { Link } from "react-router-dom";

/**
 * Global reusable empty state used across all app pages.
 * Renders a centered card with an icon, headline, body copy and up to
 * two CTA buttons. Keeps every page's empty state visually consistent.
 *
 * Props:
 *   icon        - lucide icon component (required)
 *   title       - short headline
 *   body        - one-line explanation
 *   primary     - { label, to?, href?, onClick?, testId? } - green button
 *   secondary   - { label, to?, href?, onClick?, testId? } - outline button
 *   accent      - tailwind color class for the icon tint (default lm-primary)
 *   testId      - optional data-testid on the container
 */
export default function EmptyState({
  icon: Icon,
  title,
  body,
  primary,
  secondary,
  accentClass = "bg-lm-tint2 text-lm-primary",
  testId = "empty-state",
  className = "",
}) {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border-2 border-dashed border-lm-border bg-lm-surface p-10 md:p-14 text-center ${className}`}
    >
      {Icon && (
        <div className={`w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-5 ${accentClass}`}>
          <Icon className="w-6 h-6" strokeWidth={2.2} />
        </div>
      )}
      {title && (
        <h3 className="text-xl md:text-2xl font-display font-semibold mb-2 text-lm-text">
          {title}
        </h3>
      )}
      {body && (
        <p className="text-sm md:text-base text-lm-text2 max-w-md mx-auto mb-6 leading-relaxed">
          {body}
        </p>
      )}
      {(primary || secondary) && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {primary && <PrimaryButton {...primary} />}
          {secondary && <SecondaryButton {...secondary} />}
        </div>
      )}
    </div>
  );
}

function PrimaryButton({ label, to, href, onClick, testId }) {
  const cls =
    "inline-flex items-center gap-1.5 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-medium hover:bg-lm-primaryHover transition-colors";
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {label}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>
      {label}
    </button>
  );
}

function SecondaryButton({ label, to, href, onClick, testId }) {
  const cls =
    "inline-flex items-center gap-1.5 border border-lm-border bg-white text-lm-text rounded-lg px-5 py-2.5 font-medium hover:bg-lm-tint/50 transition-colors";
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {label}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>
      {label}
    </button>
  );
}
